'use client';

import { useEffect, useState } from 'react';
import { useQueryState } from 'nuqs';
import { Search, X, Loader2 } from 'lucide-react';

/**
 * Input de búsqueda global con debounce 250ms y URL state via `nuqs`.
 * Actualiza `?q=...` que Server Component lee para ejecutar la query.
 */
export function SearchInput({ autoFocus = false }: { autoFocus?: boolean }) {
  const [query, setQuery] = useQueryState('q', { defaultValue: '', throttleMs: 250 });
  const [local, setLocal] = useState(query);
  const [pending, setPending] = useState(false);

  // Debounce local -> URL
  useEffect(() => {
    if (local === query) return;
    setPending(true);
    const id = setTimeout(() => {
      void setQuery(local || null);
      setPending(false);
    }, 250);
    return () => clearTimeout(id);
  }, [local, query, setQuery]);

  return (
    <div className="relative">
      <Search
        className="pointer-events-none absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2 text-[var(--color-fg-muted)]"
        strokeWidth={1.25}
        aria-hidden
      />
      <input
        type="search"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        placeholder="Buscar piezas, colecciones, materiales…"
        autoFocus={autoFocus}
        aria-label="Buscar en el catálogo"
        className="type-body-lg h-14 w-full rounded-sm border border-[var(--color-border)] bg-[var(--color-surface)] pr-12 pl-12 text-[var(--color-fg)] placeholder:text-[var(--color-fg-subtle)] focus:border-[var(--color-accent)] focus:outline-none"
      />
      {pending ? (
        <Loader2
          className="absolute top-1/2 right-4 h-4 w-4 -translate-y-1/2 animate-spin text-[var(--color-fg-muted)]"
          aria-hidden
        />
      ) : local ? (
        <button
          type="button"
          onClick={() => {
            setLocal('');
            void setQuery(null);
          }}
          aria-label="Limpiar búsqueda"
          className="absolute top-1/2 right-4 -translate-y-1/2 text-[var(--color-fg-muted)] hover:text-[var(--color-accent)]"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}
